import { COLS, ROWS, LAYER_COLORS, MAX_LAYER, TOWERS, getMap, type Balloon, type TdMap, type TowerType } from "../../shared/games/taarn";
import { paintThemed } from "./taarnArtThemes";

/**
 * Procedural artwork for Tårnforsvar (everything is drawn with canvas – no image files): sunny grass, a cobblestone road,
 * lush foliage around the edges, and five original animal defenders that turn towards their target.
 */
export const CELL = 64;
export const W = COLS * CELL;
export const H = ROWS * CELL;

export function rrect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

const hash = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
/** the corner points of the map being painted (set by drawScene before the scenery is painted) */
let PTS: (readonly [number, number])[] = [];
const ptsOf = (map: TdMap) => map.waypoints.map(([x, y]) => [x * CELL + CELL / 2, y * CELL + CELL / 2] as const);

export function distToPath(px: number, py: number): number {
  let best = 1e9;
  for (let i = 0; i < PTS.length - 1; i++) {
    const [ax, ay] = PTS[i]!, [bx, by] = PTS[i + 1]!;
    const dx = bx - ax, dy = by - ay;
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
    best = Math.min(best, Math.hypot(px - (ax + t * dx), py - (ay + t * dy)));
  }
  return best;
}
function strokePath(c: CanvasRenderingContext2D, w: number, col: string | CanvasGradient) {
  c.strokeStyle = col; c.lineWidth = w; c.lineJoin = "round"; c.lineCap = "round";
  c.beginPath();
  PTS.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.stroke();
}

/* ───────────── scenery ───────────── */
/** A rosette of broad leaves – the foliage that frames the map. */
function foliage(c: CanvasRenderingContext2D, x: number, y: number, r: number, seed: number, autumn = false) {
  c.fillStyle = "rgba(8,45,18,.30)";
  c.beginPath(); c.ellipse(x + r * 0.2, y + r * 0.35, r * 1.05, r * 0.75, 0, 0, 7); c.fill();
  const palette = autumn
    ? [["#c2570f", "#e8921f"], ["#d9731a", "#f6b73a"], ["#eba12c", "#ffd866"]]
    : [["#1f7a35", "#3fae4e"], ["#2f9a45", "#62cc66"], ["#4cba55", "#9be28a"]];
  for (let layer = 0; layer < 3; layer++) {
    const n = 8 + layer * 2;
    const len = r * (1 - layer * 0.26);
    const wid = len * 0.46;
    const off = hash(seed * 3 + layer) * 6.28;
    for (let i = 0; i < n; i++) {
      const a = off + (i / n) * Math.PI * 2 + (hash(seed + i * 7 + layer) - 0.5) * 0.3;
      c.save();
      c.translate(x, y);
      c.rotate(a);
      const g = c.createLinearGradient(0, 0, len, 0);
      g.addColorStop(0, palette[layer]![0]); g.addColorStop(1, palette[layer]![1]);
      c.fillStyle = g;
      c.strokeStyle = layer === 0 ? "rgba(10,60,25,.55)" : "rgba(10,70,30,.35)";
      c.lineWidth = 1.2;
      c.beginPath();
      c.moveTo(len * 0.12, 0);
      c.bezierCurveTo(len * 0.35, -wid, len * 0.85, -wid * 0.8, len, 0);
      c.bezierCurveTo(len * 0.85, wid * 0.8, len * 0.35, wid, len * 0.12, 0);
      c.fill(); c.stroke();
      c.strokeStyle = "rgba(255,255,255,.22)";
      c.beginPath(); c.moveTo(len * 0.2, 0); c.lineTo(len * 0.9, 0); c.stroke();
      c.restore();
    }
  }
}
function bush(c: CanvasRenderingContext2D, x: number, y: number, s: number, seed: number) { foliage(c, x, y, s, seed); }
function rock(c: CanvasRenderingContext2D, x: number, y: number, s: number) {
  c.fillStyle = "rgba(10,50,20,.25)"; c.beginPath(); c.ellipse(x + 2, y + s * 0.45, s * 1.1, s * 0.3, 0, 0, 7); c.fill();
  const g = c.createLinearGradient(x - s, y - s, x + s, y + s);
  g.addColorStop(0, "#e4e7f0"); g.addColorStop(1, "#7b839c");
  c.fillStyle = g; c.strokeStyle = "#4c536b"; c.lineWidth = 1.5;
  c.beginPath(); c.moveTo(x - s, y + s * 0.4); c.quadraticCurveTo(x - s * 0.9, y - s * 0.7, x - s * 0.1, y - s * 0.65);
  c.quadraticCurveTo(x + s, y - s * 0.6, x + s, y + s * 0.4); c.closePath(); c.fill(); c.stroke();
}
function flower(c: CanvasRenderingContext2D, x: number, y: number, r: number, col: string) {
  c.fillStyle = col;
  for (let k = 0; k < 5; k++) { const a = (k / 5) * Math.PI * 2; c.beginPath(); c.arc(x + Math.cos(a) * r, y + Math.sin(a) * r, r * 0.78, 0, 7); c.fill(); }
  c.fillStyle = "#ffcf33"; c.beginPath(); c.arc(x, y, r * 0.62, 0, 7); c.fill();
}
function cobble(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, rot: number, seed: number) {
  c.save();
  c.translate(x, y);
  c.rotate(rot);
  const tone = 0.88 + hash(seed) * 0.2;
  const warm = hash(seed + 3) > 0.7;
  const top = `rgb(${Math.round((warm ? 214 : 205) * tone)},${Math.round((warm ? 208 : 210) * tone)},${Math.round((warm ? 198 : 218) * tone)})`;
  const bot = `rgb(${Math.round((warm ? 160 : 150) * tone)},${Math.round((warm ? 154 : 158) * tone)},${Math.round((warm ? 146 : 170) * tone)})`;
  const g = c.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
  g.addColorStop(0, top); g.addColorStop(1, bot);
  c.fillStyle = g;
  c.strokeStyle = "#69717e";
  c.lineWidth = 2;
  rrect(c, -w / 2, -h / 2, w, h, 7);
  c.fill(); c.stroke();
  c.strokeStyle = "rgba(255,255,255,.55)";
  c.lineWidth = 1.5;
  c.beginPath(); c.moveTo(-w / 2 + 6, -h / 2 + 3); c.lineTo(w / 2 - 6, -h / 2 + 3); c.stroke();
  c.restore();
}

const sceneCache = new Map<string, HTMLCanvasElement>();
function paintScene(c: CanvasRenderingContext2D, map: TdMap) {
  const WAYPOINTS = map.waypoints;
  // ── grass: base gradient, soft tiles, big light/dark patches, blades
  const base = c.createLinearGradient(0, 0, W, H);
  base.addColorStop(0, "#8be04a"); base.addColorStop(1, "#58c23f");
  c.fillStyle = base; c.fillRect(0, 0, W, H);
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    const v = hash(x * 31 + y * 57);
    c.fillStyle = (x + y) % 2 ? "rgba(255,255,200,.035)" : "rgba(0,70,20,.03)";
    c.fillRect(x * CELL, y * CELL, CELL, CELL);
    c.fillStyle = v > 0.5 ? `rgba(255,255,160,${0.05 * v})` : `rgba(0,80,20,${0.07 * (1 - v)})`;
    c.fillRect(x * CELL, y * CELL, CELL, CELL);
  }
  for (let i = 0; i < 34; i++) {
    const px = hash(i * 7.3) * W, py = hash(i * 3.1 + 9) * H, r = 80 + hash(i) * 130;
    const g = c.createRadialGradient(px, py, 0, px, py, r);
    g.addColorStop(0, i % 2 === 0 ? "rgba(215,255,140,.26)" : "rgba(20,110,30,.16)"); g.addColorStop(1, "rgba(0,0,0,0)");
    c.fillStyle = g; c.fillRect(px - r, py - r, r * 2, r * 2);
  }
  c.lineCap = "round";
  for (let i = 0; i < 900; i++) {
    const x = hash(i * 1.7 + 1) * W, y = hash(i * 2.3 + 5) * H;
    if (distToPath(x, y) < CELL * 0.6) continue;
    c.strokeStyle = hash(i * 9.1) > 0.5 ? "rgba(30,120,30,.5)" : "rgba(210,255,150,.65)";
    c.lineWidth = 1.5;
    for (let k = -1; k <= 1; k++) { c.beginPath(); c.moveTo(x + k * 3, y); c.quadraticCurveTo(x + k * 4, y - 5, x + k * 6, y - 9 - hash(i + k) * 4); c.stroke(); }
  }
  // ── pond in the bottom-left corner
  { const px = 62, py = H - 50;
    c.fillStyle = "rgba(10,60,25,.3)"; c.beginPath(); c.ellipse(px + 3, py + 5, 84, 50, -0.1, 0, 7); c.fill();
    c.fillStyle = "#c9a56a"; c.beginPath(); c.ellipse(px, py, 80, 47, -0.1, 0, 7); c.fill();
    const g = c.createLinearGradient(px, py - 40, px, py + 40); g.addColorStop(0, "#9aeaff"); g.addColorStop(1, "#2a93e6");
    c.fillStyle = g; c.beginPath(); c.ellipse(px, py, 70, 39, -0.1, 0, 7); c.fill();
    c.strokeStyle = "rgba(255,255,255,.75)"; c.lineWidth = 2;
    c.beginPath(); c.ellipse(px - 10, py - 6, 26, 8, 0, 3.4, 5.6); c.stroke();
    c.beginPath(); c.ellipse(px + 18, py + 10, 18, 5, 0, 0.2, 2.6); c.stroke();
    c.fillStyle = "#3fbf57"; c.strokeStyle = "#1c7a33"; c.lineWidth = 1.5; c.beginPath(); c.ellipse(px + 30, py - 4, 12, 7.5, 0.3, 0, 7); c.fill(); c.stroke();
    c.fillStyle = "#ff9fc5"; c.beginPath(); c.arc(px + 30, py - 6, 4.5, 0, 7); c.fill();
  }
  // ── road: grass shadow, dark edge, grout, then hand-laid cobblestones
  strokePath(c, CELL * 1.14, "rgba(20,90,30,.32)");
  strokePath(c, CELL * 0.99, "#5d6572");
  strokePath(c, CELL * 0.9, "#8f97a5");
  const step = 25;
  for (let gy = -step; gy < H + step; gy += step) for (let gx = -step; gx < W + step; gx += step) {
    const i = Math.round(gx / step) * 131 + Math.round(gy / step) * 17;
    const x = gx + (hash(i) - 0.5) * 8 + (Math.round(gy / step) % 2 ? step / 2 : 0), y = gy + (hash(i + 1) - 0.5) * 8;
    if (distToPath(x, y) > CELL * 0.43) continue;
    cobble(c, x, y, 21 + hash(i + 2) * 7, 17 + hash(i + 3) * 6, (hash(i + 4) - 0.5) * 0.45, i);
  }
  // grass tufts poking over the road edge
  for (let i = 0; i < 260; i++) {
    const x = hash(i * 2.2 + 4) * W, y = hash(i * 3.3 + 1) * H;
    const d = distToPath(x, y);
    if (d < CELL * 0.43 || d > CELL * 0.58) continue;
    c.strokeStyle = hash(i) > 0.5 ? "#2f9a35" : "#8be04a"; c.lineWidth = 2;
    for (let k = -1; k <= 1; k++) { c.beginPath(); c.moveTo(x + k * 3, y + 2); c.quadraticCurveTo(x + k * 4, y - 4, x + k * 7, y - 9 - hash(i + k) * 4); c.stroke(); }
  }
  // ── flowers, bushes, rocks off the road
  const spots = (n: number, seed: number, minD: number, f: (x: number, y: number, i: number) => void) => {
    for (let i = 0; i < n; i++) { const x = hash(i * 3.7 + seed) * W, y = hash(i * 5.9 + seed * 2) * H; if (distToPath(x, y) > minD) f(x, y, i); }
  };
  spots(130, 11, CELL * 0.62, (x, y, i) => flower(c, x, y, 2.4 + hash(i) * 1.4, ["#fff", "#fff", "#ffe066", "#fff", "#ffb3d1"][i % 5]!));
  spots(12, 21, CELL * 0.95, (x, y, i) => bush(c, x, y, 14 + hash(i) * 5, i));
  spots(8, 31, CELL * 0.8, (x, y) => rock(c, x, y, 9));
  // ── leafy frame around the whole map
  const frame: [number, number, number][] = [];
  for (let x = -6; x < W + 20; x += 38 + hash(x) * 16) frame.push([x, -6 + hash(x * 2) * 14, 40 + hash(x * 3) * 18]);
  for (let x = 20; x < W - 140; x += 44 + hash(x) * 20) frame.push([x, H + 4 - hash(x * 2) * 10, 38 + hash(x * 3) * 16]);
  for (let y = 40; y < H; y += 48 + hash(y) * 18) frame.push([W + 2 - hash(y) * 8, y, 40 + hash(y * 3) * 14]);
  for (let y = 120; y < H - 120; y += 56 + hash(y) * 20) frame.push([-4 + hash(y) * 8, y, 36 + hash(y * 3) * 14]);
  frame.push([20, 14, 64], [W - 20, 12, 66], [W - 14, H - 130, 60], [6, 150, 52]);
  frame.forEach(([x, y, r], i) => { if (distToPath(x, y) > CELL * 0.8 + r * 0.25) foliage(c, x, y, r, i * 5 + 1); });
  // ── cave entrance (stone arch)
  { const cy = PTS[0]![1];
    c.fillStyle = "rgba(0,0,0,.25)"; rrect(c, -2, cy - 36, 52, 80, 22); c.fill();
    const sg = c.createLinearGradient(0, cy - 40, 0, cy + 40); sg.addColorStop(0, "#c9cedd"); sg.addColorStop(1, "#7a8299");
    c.fillStyle = sg; rrect(c, -8, cy - 40, 52, 76, 22); c.fill();
    c.strokeStyle = "#394058"; c.lineWidth = 3; c.stroke();
    const g = c.createRadialGradient(8, cy, 2, 8, cy, 32); g.addColorStop(0, "#000"); g.addColorStop(1, "#2a1d12");
    c.fillStyle = g; rrect(c, -8, cy - 29, 36, 58, 17); c.fill();
    c.fillStyle = "#9aa2bb"; for (let k = -1; k <= 1; k++) { c.beginPath(); c.arc(38, cy + k * 22, 4, 0, 7); c.fill(); c.strokeStyle = "#394058"; c.lineWidth = 1.5; c.stroke(); }
  }
  // ── castle (goal)
  { const [bx, by] = WAYPOINTS[WAYPOINTS.length - 1]!; const cx = bx * CELL + CELL / 2 - 6, cy = by * CELL + CELL / 2 + 4;
    c.fillStyle = "rgba(10,40,20,.3)"; c.beginPath(); c.ellipse(cx + 6, cy + 30, 46, 13, 0, 0, 7); c.fill();
    const wall = c.createLinearGradient(cx - 30, 0, cx + 30, 0); wall.addColorStop(0, "#eef0fa"); wall.addColorStop(1, "#a3abc8");
    c.strokeStyle = "#2c3150"; c.lineWidth = 3;
    const tw = (x: number, h: number) => {
      c.fillStyle = wall; rrect(c, x - 11, cy - h, 22, h + 26, 3); c.fill(); c.stroke();
      c.fillStyle = "#c2c9e2";
      for (let k = -1; k <= 1; k++) { c.fillRect(x + k * 8 - 3, cy - h - 8, 6, 8); c.strokeRect(x + k * 8 - 3, cy - h - 8, 6, 8); }
      c.fillStyle = "#2c3150"; rrect(c, x - 2.5, cy - h + 12, 5, 11, 2); c.fill();
      c.fillStyle = "#e0483c"; c.beginPath(); c.moveTo(x - 12, cy - h - 8); c.lineTo(x, cy - h - 24); c.lineTo(x + 12, cy - h - 8); c.closePath(); c.fill(); c.stroke();
    };
    c.fillStyle = wall; rrect(c, cx - 28, cy - 14, 56, 40, 4); c.fill(); c.stroke();
    tw(cx - 26, 34); tw(cx + 26, 34);
    c.fillStyle = "#c2c9e2"; for (let k = -1; k <= 1; k++) { c.fillRect(cx + k * 14 - 4, cy - 22, 8, 8); c.strokeRect(cx + k * 14 - 4, cy - 22, 8, 8); }
    c.fillStyle = "#7b4b1d"; c.beginPath(); c.moveTo(cx - 10, cy + 26); c.lineTo(cx - 10, cy + 6); c.arc(cx, cy + 6, 10, Math.PI, 0); c.lineTo(cx + 10, cy + 26); c.closePath(); c.fill(); c.stroke();
    c.strokeStyle = "#4a2c0e"; c.lineWidth = 1.5; for (const dx of [-5, 0, 5]) { c.beginPath(); c.moveTo(cx + dx, cy - 2); c.lineTo(cx + dx, cy + 26); c.stroke(); }
    c.strokeStyle = "#2c3150"; c.lineWidth = 3; c.beginPath(); c.moveTo(cx, cy - 22); c.lineTo(cx, cy - 58); c.stroke();
    c.fillStyle = "#ef4444"; c.beginPath(); c.moveTo(cx, cy - 58); c.quadraticCurveTo(cx + 12, cy - 56, cx + 22, cy - 50); c.quadraticCurveTo(cx + 12, cy - 46, cx, cy - 44); c.closePath(); c.fill(); c.stroke();
  }
  // ── light: warm sun glow top-left, soft vignette
  const sun = c.createRadialGradient(0, 0, 0, 0, 0, W * 0.8); sun.addColorStop(0, "rgba(255,250,190,.30)"); sun.addColorStop(1, "rgba(255,250,190,0)");
  c.fillStyle = sun; c.fillRect(0, 0, W, H);
  const vig = c.createRadialGradient(W / 2, H / 2, H * 0.5, W / 2, H / 2, W * 0.78); vig.addColorStop(0, "rgba(0,40,20,0)"); vig.addColorStop(1, "rgba(0,40,20,.24)");
  c.fillStyle = vig; c.fillRect(0, 0, W, H);
}
export function drawScene(c: CanvasRenderingContext2D, mapIn?: TdMap) {
  const map = mapIn ?? getMap("skov");
  let cv = sceneCache.get(map.id);
  if (!cv) {
    cv = document.createElement("canvas");
    cv.width = W; cv.height = H;
    PTS = ptsOf(map).slice();
    if (map.id === "skov") paintScene(cv.getContext("2d")!, map);
    else paintThemed(cv.getContext("2d")!, map, { pts: PTS, dist: distToPath });
    sceneCache.set(map.id, cv);
  }
  c.drawImage(cv, 0, 0);
}

/* ───────────── the defenders ───────────── */
const INK = "#2a1f3d";
function ellipse(c: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, fill: string | CanvasGradient, stroke = INK, lw = 2, rot = 0) {
  c.fillStyle = fill; c.strokeStyle = stroke; c.lineWidth = lw;
  c.beginPath(); c.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2); c.fill(); if (lw > 0) c.stroke();
}
function shaded(c: CanvasRenderingContext2D, x: number, y: number, r: number, light: string, dark: string) {
  const g = c.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r * 1.1);
  g.addColorStop(0, light); g.addColorStop(1, dark);
  return g;
}
function eyes(c: CanvasRenderingContext2D, x: number, y: number, spread: number, r = 2.6) {
  for (const s of [-1, 1]) {
    c.fillStyle = "#fff"; c.beginPath(); c.arc(x, y + s * spread, r, 0, 7); c.fill();
    c.fillStyle = INK; c.beginPath(); c.arc(x + 0.8, y + s * spread, r * 0.62, 0, 7); c.fill();
  }
}
function triangle(c: CanvasRenderingContext2D, ax: number, ay: number, bx: number, by: number, cx: number, cy: number, fill: string) {
  c.fillStyle = fill; c.strokeStyle = INK; c.lineWidth = 2;
  c.beginPath(); c.moveTo(ax, ay); c.lineTo(bx, by); c.lineTo(cx, cy); c.closePath(); c.fill(); c.stroke();
}

/** Characters face +x. `t` = seconds (idle animation), `recoil` 0..1 right after a shot. */
function drawChar(c: CanvasRenderingContext2D, type: TowerType, level: number, t: number, recoil: number) {
  const bob = Math.sin(t * 3) * 0.8;
  c.translate(0, bob);
  const back = -recoil * 5;
  switch (type) {
    case "pil": { // fox archer
      const sway = Math.sin(t * 4) * 4;
      c.fillStyle = "#e8761e"; c.strokeStyle = INK; c.lineWidth = 2;
      c.beginPath(); c.ellipse(-17, sway * 0.5, 14, 6.5, sway * 0.03, 0, 7); c.fill(); c.stroke();
      ellipse(c, -25, sway * 0.7, 5, 4.5, "#fff", INK, 1.5);
      if (level >= 2) { c.fillStyle = "#7a4a1e"; c.save(); c.rotate(-0.5); rrect(c, -14, -18, 8, 20, 3); c.fill(); c.stroke(); c.fillStyle = "#fff"; for (const dx of [-12, -9, -6]) c.fillRect(dx, -22, 1.6, 6); c.restore(); }
      ellipse(c, 0, 0, 14, 12, shaded(c, 0, 0, 14, "#ffa24a", "#d9620f"), INK, 2);
      ellipse(c, 3, 0, 7, 6, "#ffe6c7", "transparent", 0);
      if (level >= 2) { c.fillStyle = "#e0483c"; c.strokeStyle = INK; c.lineWidth = 2; rrect(c, 4, -13, 6, 26, 3); c.fill(); c.stroke(); }
      // bow
      c.strokeStyle = level >= 3 ? "#ffd23f" : "#7a4a1e"; c.lineWidth = 4.5; c.lineCap = "round";
      c.beginPath(); c.arc(18 + back * 0.4, 0, 17, -1.15, 1.15); c.stroke();
      c.strokeStyle = INK; c.lineWidth = 1; c.beginPath(); c.arc(18 + back * 0.4, 0, 17.8, -1.15, 1.15); c.stroke();
      c.strokeStyle = "#f4efe0"; c.lineWidth = 1.5; c.beginPath(); c.moveTo(18 + back * 0.4 + 17 * Math.cos(-1.15), -17 * Math.sin(1.15)); c.lineTo(8 + back, 0); c.lineTo(18 + back * 0.4 + 17 * Math.cos(1.15), 17 * Math.sin(1.15)); c.stroke();
      c.strokeStyle = "#8a5a2b"; c.lineWidth = 2.2; c.beginPath(); c.moveTo(8 + back, 0); c.lineTo(34 + back, 0); c.stroke();
      triangle(c, 38 + back, 0, 33 + back, -3.5, 33 + back, 3.5, "#cfd5e3");
      triangle(c, 8 + back, 0, 4 + back, -3, 4 + back, 3, "#e0483c");
      // head
      triangle(c, 0, -8, -5, -17, 5, -13, "#e8761e"); triangle(c, 0, 8, -5, 17, 5, 13, "#e8761e");
      ellipse(c, 4, 0, 11, 10.5, shaded(c, 4, 0, 11, "#ffb15c", "#dd6a14"), INK, 2);
      if (level >= 3) { c.fillStyle = "#fff"; c.strokeStyle = INK; c.lineWidth = 1.5; c.beginPath(); c.ellipse(-3, -3, 7, 2.5, 0.5, 0, 7); c.fill(); c.stroke(); ellipse(c, -1, 6, 2.2, 5, "#58c23f", INK, 1.2, 0.4); }
      ellipse(c, 12, 0, 6.5, 5, "#fff3e0", INK, 1.5);
      c.fillStyle = INK; c.beginPath(); c.arc(17, 0, 2.4, 0, 7); c.fill();
      eyes(c, 8, 0, 4.6);
      break; }
    case "kanon": { // bear with cannon
      ellipse(c, -4, 0, 17, 15, shaded(c, -4, 0, 16, "#c38a55", "#7a4a26"), INK, 2);
      ellipse(c, -3, -17, 5, 4.5, "#2e2a3a", INK, 2); ellipse(c, -3, 17, 5, 4.5, "#2e2a3a", INK, 2); // wheels
      // cannon
      const len = 28, bw = level >= 3 ? 17 : 14;
      const bg = c.createLinearGradient(0, -bw / 2, 0, bw / 2); bg.addColorStop(0, "#8a93b3"); bg.addColorStop(0.5, "#454c68"); bg.addColorStop(1, "#262b45");
      c.fillStyle = bg; c.strokeStyle = INK; c.lineWidth = 2.5;
      rrect(c, 4 + back, -bw / 2, len, bw, 4); c.fill(); c.stroke();
      c.fillStyle = level >= 3 ? "#ffd23f" : "#d9a441"; c.strokeStyle = INK; c.lineWidth = 2;
      rrect(c, 26 + back, -bw / 2 - 1.5, 6, bw + 3, 2); c.fill(); c.stroke();
      if (level >= 2) { rrect(c, 12 + back, -bw / 2 - 1.5, 5, bw + 3, 2); c.fill(); c.stroke(); }
      c.fillStyle = "#12142a"; c.beginPath(); c.ellipse(32 + back, 0, 2.5, bw / 2 - 3, 0, 0, 7); c.fill();
      // paws + head
      ellipse(c, 6, -10, 4.5, 4.5, "#a8703d", INK, 2); ellipse(c, 6, 10, 4.5, 4.5, "#a8703d", INK, 2);
      ellipse(c, -2, -9, 4.4, 4.4, "#8a5a2e", INK, 2); ellipse(c, -2, 9, 4.4, 4.4, "#8a5a2e", INK, 2);
      ellipse(c, -6, 0, 11, 10.5, shaded(c, -6, 0, 11, "#d89a62", "#8a5a2e"), INK, 2);
      ellipse(c, -4, -7.5, 4, 4, "#8a5a2e", INK, 2); ellipse(c, -4, 7.5, 4, 4, "#8a5a2e", INK, 2);
      ellipse(c, 1, 0, 5.5, 4.6, "#f0d2a8", INK, 1.5); c.fillStyle = INK; c.beginPath(); c.arc(4.5, 0, 2, 0, 7); c.fill();
      eyes(c, -1, 0, 4.2, 2.3);
      if (level >= 2) { const hg = c.createRadialGradient(-8, -3, 1, -6, 0, 12); hg.addColorStop(0, "#c9d0e6"); hg.addColorStop(1, "#6a7390"); c.fillStyle = hg; c.strokeStyle = INK; c.lineWidth = 2; c.beginPath(); c.arc(-7, 0, 9.5, Math.PI * 0.55, Math.PI * 1.45); c.closePath(); c.fill(); c.stroke(); }
      break; }
    case "is": { // penguin ice mage
      const spin = t * 1.2;
      c.save(); c.globalAlpha = 0.55; c.strokeStyle = "#bff2ff"; c.lineWidth = 2;
      for (let k = 0; k < 6; k++) { const a = spin + (k / 6) * Math.PI * 2; c.beginPath(); c.moveTo(Math.cos(a) * 22, Math.sin(a) * 22); c.lineTo(Math.cos(a) * 28, Math.sin(a) * 28); c.stroke(); }
      c.restore();
      ellipse(c, -3, -13, 6.5, 3.5, "#1f3b73", INK, 2, 0.5); ellipse(c, -3, 13, 6.5, 3.5, "#1f3b73", INK, 2, -0.5); // flippers
      ellipse(c, -9, -5, 4, 2.6, "#ff9d2e", INK, 1.5); ellipse(c, -9, 5, 4, 2.6, "#ff9d2e", INK, 1.5);
      ellipse(c, -2, 0, 14, 12, shaded(c, -2, 0, 14, "#4b79c9", "#1b3566"), INK, 2);
      ellipse(c, 2, 0, 8, 7, "#f4fbff", "transparent", 0);
      if (level >= 2) { c.fillStyle = "#2fb6ff"; c.strokeStyle = INK; c.lineWidth = 2; rrect(c, 3, -12, 6, 24, 3); c.fill(); c.stroke(); }
      // crystal
      const cr = level >= 3 ? 13 : level >= 2 ? 11 : 9;
      const gl = c.createRadialGradient(26 + back, 0, 1, 26 + back, 0, cr * 2.4); gl.addColorStop(0, "rgba(170,235,255,.85)"); gl.addColorStop(1, "rgba(170,235,255,0)");
      c.fillStyle = gl; c.beginPath(); c.arc(26 + back, 0, cr * 2.4, 0, 7); c.fill();
      c.save(); c.translate(26 + back, 0); c.rotate(Math.sin(t * 2) * 0.15);
      const cg = c.createLinearGradient(-cr, -cr, cr, cr); cg.addColorStop(0, "#f2fdff"); cg.addColorStop(0.5, "#7fdcff"); cg.addColorStop(1, "#2d9be0");
      c.fillStyle = cg; c.strokeStyle = INK; c.lineWidth = 2;
      c.beginPath(); c.moveTo(cr * 1.2, 0); c.lineTo(0, -cr * 0.8); c.lineTo(-cr * 0.8, 0); c.lineTo(0, cr * 0.8); c.closePath(); c.fill(); c.stroke();
      c.strokeStyle = "rgba(255,255,255,.8)"; c.lineWidth = 1.5; c.beginPath(); c.moveTo(-cr * 0.5, 0); c.lineTo(cr * 0.9, 0); c.stroke();
      c.restore();
      c.strokeStyle = "#8a5a2e"; c.lineWidth = 3; c.beginPath(); c.moveTo(6, 8); c.lineTo(20 + back, 2); c.stroke();
      // head
      ellipse(c, 5, 0, 9.5, 9, shaded(c, 5, 0, 10, "#3a5fa8", "#17305f"), INK, 2);
      ellipse(c, 8, 0, 5, 6.5, "#f4fbff", "transparent", 0);
      triangle(c, 17, 0, 11, -3.4, 11, 3.4, "#ff9d2e");
      eyes(c, 8.5, 0, 4.4, 2.4);
      if (level >= 3) { c.fillStyle = "#bff2ff"; c.strokeStyle = INK; c.lineWidth = 1.5; for (const dy of [-4, 0, 4]) { c.beginPath(); c.moveTo(0, dy + 2); c.lineTo(-4, dy); c.lineTo(0, dy - 2); c.closePath(); c.fill(); c.stroke(); } }
      break; }
    case "snigskytte": { // owl sniper
      // wings
      for (const s of [-1, 1]) {
        c.fillStyle = shaded(c, -2, s * 12, 14, "#c79b6b", "#7d5632"); c.strokeStyle = INK; c.lineWidth = 2;
        c.beginPath(); c.moveTo(6, s * 6); c.quadraticCurveTo(0, s * 22, -18, s * 20); c.quadraticCurveTo(-12, s * 8, -8, s * 5); c.closePath(); c.fill(); c.stroke();
        c.strokeStyle = "rgba(60,30,10,.5)"; c.lineWidth = 1.2;
        for (let k = 0; k < 3; k++) { c.beginPath(); c.moveTo(-2 - k * 5, s * (9 + k * 3)); c.lineTo(-9 - k * 4, s * (18 + k)); c.stroke(); }
      }
      ellipse(c, 0, 0, 12, 10, shaded(c, 0, 0, 12, "#d7b48a", "#8a6038"), INK, 2);
      // rifle
      c.fillStyle = "#3a3f5c"; c.strokeStyle = INK; c.lineWidth = 2;
      rrect(c, 6 + back, -2.6, 38, 5.2, 2); c.fill(); c.stroke();
      c.fillStyle = level >= 3 ? "#ffd23f" : "#5a4a8a"; rrect(c, 14 + back, -5.5, 13, 5, 2); c.fill(); c.stroke();
      c.fillStyle = "#7fdcff"; c.beginPath(); c.arc(28 + back, -3, 2, 0, 7); c.fill();
      c.fillStyle = "#12142a"; c.fillRect(43 + back, -1.6, 3, 3.2);
      // head
      triangle(c, 0, -7, -3, -15, 4, -11, "#8a6038"); triangle(c, 0, 7, -3, 15, 4, 11, "#8a6038");
      ellipse(c, 4, 0, 10, 9.5, shaded(c, 4, 0, 10, "#e3c79f", "#9a6e42"), INK, 2);
      for (const s of [-1, 1]) { ellipse(c, 8, s * 4.6, 4.6, 4.2, "#fff6d8", INK, 1.5); ellipse(c, 9, s * 4.6, 2.5, 2.5, "#ffb300", INK, 1); c.fillStyle = INK; c.beginPath(); c.arc(9.6, s * 4.6, 1.2, 0, 7); c.fill(); }
      triangle(c, 17, 0, 12, -2.6, 12, 2.6, "#ff9d2e");
      if (level >= 2) { c.strokeStyle = "#e0483c"; c.lineWidth = 3.5; c.beginPath(); c.moveTo(-1, -9); c.lineTo(-1, 9); c.stroke(); }
      break; }
    case "trold": { // hedgehog wizard
      c.fillStyle = "#6b4426"; c.strokeStyle = INK; c.lineWidth = 1.6;
      for (let k = 0; k < 17; k++) { const a = Math.PI * 0.5 + (k / 16) * Math.PI; const rr = 17 + hash(k) * 3; c.beginPath(); c.moveTo(Math.cos(a - 0.14) * 11, Math.sin(a - 0.14) * 11); c.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); c.lineTo(Math.cos(a + 0.14) * 11, Math.sin(a + 0.14) * 11); c.fill(); c.stroke(); }
      ellipse(c, 0, 0, 13, 12, shaded(c, 0, 0, 13, "#b98456", "#7a4d2a"), INK, 2);
      // wand + orb
      c.strokeStyle = "#7a4a1e"; c.lineWidth = 3.2; c.beginPath(); c.moveTo(4, 9); c.lineTo(24 + back, 1); c.stroke();
      const orbR = level >= 3 ? 8 : level >= 2 ? 7 : 6;
      const pulse = 1 + Math.sin(t * 5) * 0.12;
      const og = c.createRadialGradient(28 + back, 0, 1, 28 + back, 0, orbR * 2.8 * pulse); og.addColorStop(0, "rgba(255,150,220,.9)"); og.addColorStop(1, "rgba(255,150,220,0)");
      c.fillStyle = og; c.beginPath(); c.arc(28 + back, 0, orbR * 2.8 * pulse, 0, 7); c.fill();
      ellipse(c, 28 + back, 0, orbR, orbR, shaded(c, 28 + back, 0, orbR, "#ffe6f7", "#e24ba9"), INK, 2);
      if (level >= 2) for (let k = 0; k < (level >= 3 ? 3 : 1); k++) { const a = t * 2.4 + (k / 3) * Math.PI * 2; ellipse(c, 28 + back + Math.cos(a) * 13, Math.sin(a) * 13, 2.2, 2.2, "#fff", "transparent", 0); }
      // face + hat
      ellipse(c, 8, 0, 6.5, 5.6, "#f3d7b3", INK, 1.5); c.fillStyle = INK; c.beginPath(); c.arc(12.5, 0, 2, 0, 7); c.fill();
      eyes(c, 6, 0, 4.4, 2.2);
      const hg = c.createRadialGradient(-2, -3, 1, 0, 0, 14); hg.addColorStop(0, "#b27bff"); hg.addColorStop(1, "#5a2fa8");
      c.fillStyle = hg; c.strokeStyle = INK; c.lineWidth = 2.2; c.beginPath(); c.arc(-1, 0, 13, 0, 7); c.fill(); c.stroke();
      c.fillStyle = "#7b4fd4"; c.beginPath(); c.arc(-3, 0, 7, 0, 7); c.fill(); c.stroke();
      c.strokeStyle = "#ffd23f"; c.lineWidth = 3; c.beginPath(); c.arc(-1, 0, 10, -0.9, 0.9); c.stroke();
      if (level >= 3) { c.fillStyle = "#ffd23f"; c.beginPath(); for (let k = 0; k < 10; k++) { const r = k % 2 ? 2 : 4.4, a = (k / 10) * Math.PI * 2 - Math.PI / 2; c.lineTo(-3 + Math.cos(a) * r, Math.sin(a) * r); } c.closePath(); c.fill(); }
      break; }
    default: { // the unlockable defenders: a round critter in the tower's colour with its emoji (the 3D view has the real models)
      const def = TOWERS[type];
      const col = def.color;
      ellipse(c, -2, 0, 17, 15, shaded(c, -2, -2, 17, "#ffffff", col), INK, 2.4);
      ellipse(c, 10, 0, 12, 11, shaded(c, 10, -1, 12, "#ffffff", col), INK, 2.2);
      eyes(c, 15, 0, 4.5, 2.4);
      c.save();
      c.font = "20px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif"; c.textAlign = "center"; c.textBaseline = "middle";
      c.fillText(def.icon, -9, 2 + back * 0.3);
      c.restore();
      if (level >= 2) { c.strokeStyle = "#ffd23f"; c.lineWidth = 2.4; c.beginPath(); c.arc(-2, 0, 20, 0, 7); c.stroke(); }
      break; }
  }
}

export interface DrawTowerOpts { ghost?: boolean; boost?: boolean; recoil?: number; time?: number }
/** x,y in cell units (top-left of the cell). */
export function drawTower(c: CanvasRenderingContext2D, t: { type: TowerType; level: number; x: number; y: number }, angle: number, o: DrawTowerOpts = {}) {
  const cx = t.x * CELL + CELL / 2, cy = t.y * CELL + CELL / 2;
  c.save();
  c.globalAlpha = o.ghost ? 0.8 : 1;
  c.fillStyle = "rgba(8,45,18,.38)";
  c.beginPath(); c.ellipse(cx + 3, cy + 22, 29, 11, 0, 0, 7); c.fill();
  // little earth mound with a level-coloured rim
  const mg = c.createRadialGradient(cx - 6, cy - 8, 4, cx, cy, 30);
  mg.addColorStop(0, "#a8d86a"); mg.addColorStop(1, "#4f9a35");
  c.fillStyle = mg; c.beginPath(); c.arc(cx, cy + 2, 27, 0, 7); c.fill();
  c.strokeStyle = t.level >= 3 ? "#ffd23f" : t.level === 2 ? "#e3e8f5" : "#2f7a2a"; c.lineWidth = t.level >= 2 ? 4 : 3;
  c.stroke();
  if (o.boost) {
    const g = c.createRadialGradient(cx, cy, 6, cx, cy, 44); g.addColorStop(0, "rgba(255,230,90,.55)"); g.addColorStop(1, "rgba(255,230,90,0)");
    c.fillStyle = g; c.beginPath(); c.arc(cx, cy, 44, 0, 7); c.fill();
  }
  c.translate(cx, cy - 1);
  c.rotate(angle);
  drawChar(c, t.type, t.level, o.time ?? 0, o.recoil ?? 0);
  c.restore();
  // level stars
  for (let i = 0; i < t.level; i++) {
    const sx = cx - (t.level - 1) * 6 + i * 12, sy = cy + 26;
    c.fillStyle = "#ffd23f"; c.strokeStyle = INK; c.lineWidth = 1.5;
    c.beginPath();
    for (let k = 0; k < 10; k++) { const r = k % 2 ? 2.8 : 5.4, a = (k / 10) * Math.PI * 2 - Math.PI / 2; c.lineTo(sx + Math.cos(a) * r, sy + Math.sin(a) * r); }
    c.closePath(); c.fill(); c.stroke();
  }
}

const portraitCache = new Map<string, string>();
/** Small picture of a defender for the shop cards. */
export function towerPortrait(type: TowerType, level = 1): string {
  const key = `${type}:${level}`;
  const hit = portraitCache.get(key);
  if (hit) return hit;
  const cv = document.createElement("canvas");
  cv.width = 96; cv.height = 96;
  const c = cv.getContext("2d")!;
  c.translate(48, 50);
  c.scale(1.12, 1.12);
  c.rotate(-0.5);
  drawChar(c, type, level, 0.6, 0);
  const url = cv.toDataURL("image/png");
  portraitCache.set(key, url);
  return url;
}

/* ───────────── balloons ───────────── */
export function drawBalloon(c: CanvasRenderingContext2D, b: Balloon, d: number, tick: number, frozen: boolean, map?: TdMap) {
  const [px, py] = (map ?? getMap("skov")).posAt(d);
  const x = px * CELL, y = py * CELL;
  const r = b.boss ? 27 : 13.5 + Math.min(4, b.layers * 0.5);
  const base = b.lead ? "#9aa1b6" : LAYER_COLORS[Math.min(MAX_LAYER, Math.max(1, b.layers))]!;
  c.fillStyle = "rgba(8,45,18,.30)";
  c.beginPath(); c.ellipse(x + 3, y + 12, r * 0.8, r * 0.32, 0, 0, 7); c.fill();
  c.save();
  c.translate(x, y - r * 0.5);
  c.rotate(Math.sin((tick + b.id * 3) / 5) * 0.06);
  // string
  c.strokeStyle = "rgba(40,30,70,.6)"; c.lineWidth = 1.4;
  c.beginPath(); c.moveTo(0, r * 1.15); c.quadraticCurveTo(5, r * 1.8, -2, r * 2.3); c.stroke();
  // body
  const g = c.createRadialGradient(-r * 0.38, -r * 0.45, r * 0.08, 0, 0, r * 1.25);
  g.addColorStop(0, "#fff"); g.addColorStop(0.2, base); g.addColorStop(1, b.lead ? "#4b5163" : shade(base));
  c.fillStyle = g; c.strokeStyle = "#2a1f3d"; c.lineWidth = b.boss ? 4 : 2.4;
  c.beginPath(); c.ellipse(0, 0, r * 0.88, r * 1.08, 0, 0, Math.PI * 2); c.fill(); c.stroke();
  // knot
  c.fillStyle = b.lead ? "#4b5163" : shade(base);
  c.beginPath(); c.moveTo(-3.5, r * 1.07); c.lineTo(3.5, r * 1.07); c.lineTo(0, r * 1.32); c.closePath(); c.fill(); c.stroke();
  if (b.lead) { c.strokeStyle = "rgba(255,255,255,.35)"; c.lineWidth = 1.5; for (const k of [-0.5, 0.5]) { c.beginPath(); c.ellipse(0, 0, r * 0.88, r * 1.08, 0, k * 1.2 + 0.4, k * 1.2 + 1.2); c.stroke(); } }
  // gloss
  c.fillStyle = "rgba(255,255,255,.75)";
  c.beginPath(); c.ellipse(-r * 0.36, -r * 0.46, r * 0.18, r * 0.32, -0.5, 0, 7); c.fill();
  if (frozen) { c.fillStyle = "rgba(160,230,255,.55)"; c.beginPath(); c.ellipse(0, 0, r * 0.88, r * 1.08, 0, 0, 7); c.fill(); c.strokeStyle = "#e8fbff"; c.lineWidth = 2; c.stroke(); }
  if (b.boss) {
    c.fillStyle = "#fff"; c.strokeStyle = "#2a1f3d"; c.lineWidth = 2;
    c.beginPath(); c.arc(-9, -3, 5.5, 0, 7); c.arc(9, -3, 5.5, 0, 7); c.fill(); c.stroke();
    c.fillStyle = "#2a1f3d"; c.beginPath(); c.arc(-8, -2, 2.6, 0, 7); c.arc(10, -2, 2.6, 0, 7); c.fill();
    c.lineWidth = 3; c.beginPath(); c.moveTo(-15, -11); c.lineTo(-4, -7); c.moveTo(15, -11); c.lineTo(4, -7); c.stroke();
    c.fillRect(-12, 9, 24, 4);
  }
  if (b.layers > 1 || b.boss) {
    c.fillStyle = "#2a1f3d";
    c.font = `900 ${b.boss ? 16 : 11}px Outfit, sans-serif`;
    c.textAlign = "center";
    c.fillText(String(b.layers), 0, b.boss ? 26 : 4);
  }
  c.restore();
  if (b.boss) {
    c.fillStyle = "rgba(30,20,70,.85)"; rrect(c, x - 32, y - 56, 64, 9, 4); c.fill();
    c.fillStyle = "#3fdc54"; rrect(c, x - 30, y - 54, 60 * (b.layers / b.max), 5, 2); c.fill();
  }
}
function shade(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.max(0, Math.round(v * 0.62));
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
}

/* ───────────── effects ───────────── */
export type FxKind = "arrow" | "ball" | "orb" | "snipe" | "nova" | "pop" | "boom" | "text" | "flash";
export interface Fx {
  kind: FxKind;
  x: number; y: number; x2?: number; y2?: number;
  born: number; life: number;
  color: string;
  big?: boolean;
  text?: string;
}

export function drawFx(c: CanvasRenderingContext2D, f: Fx, now: number) {
  const k = Math.max(0, Math.min(1, (now - f.born) / f.life));
  c.save();
  switch (f.kind) {
    case "arrow": {
      const x = f.x + (f.x2! - f.x) * k, y = f.y + (f.y2! - f.y) * k;
      const a = Math.atan2(f.y2! - f.y, f.x2! - f.x);
      c.translate(x, y); c.rotate(a);
      c.strokeStyle = "#7a4a1e"; c.lineWidth = 2.5; c.beginPath(); c.moveTo(-14, 0); c.lineTo(4, 0); c.stroke();
      c.fillStyle = "#dfe5f2"; c.strokeStyle = INK; c.lineWidth = 1; c.beginPath(); c.moveTo(10, 0); c.lineTo(3, -3.5); c.lineTo(3, 3.5); c.closePath(); c.fill(); c.stroke();
      c.fillStyle = "#e0483c"; c.beginPath(); c.moveTo(-14, 0); c.lineTo(-18, -3.5); c.lineTo(-10, 0); c.lineTo(-18, 3.5); c.closePath(); c.fill();
      break; }
    case "ball": {
      const x = f.x + (f.x2! - f.x) * k, y = f.y + (f.y2! - f.y) * k - Math.sin(k * Math.PI) * 26;
      c.fillStyle = "rgba(0,0,0,.25)"; c.beginPath(); c.ellipse(f.x + (f.x2! - f.x) * k, f.y + (f.y2! - f.y) * k + 6, 7, 3.5, 0, 0, 7); c.fill();
      const g = c.createRadialGradient(x - 2, y - 2, 1, x, y, 8); g.addColorStop(0, "#8a8fa8"); g.addColorStop(1, "#14172b");
      c.fillStyle = g; c.beginPath(); c.arc(x, y, 7.5, 0, 7); c.fill();
      break; }
    case "orb": {
      const x = f.x + (f.x2! - f.x) * k, y = f.y + (f.y2! - f.y) * k;
      for (let i = 4; i >= 0; i--) { const kk = Math.max(0, k - i * 0.07); c.globalAlpha = 0.18 * (5 - i); c.fillStyle = f.color; c.beginPath(); c.arc(f.x + (f.x2! - f.x) * kk, f.y + (f.y2! - f.y) * kk, 7 - i * 0.8, 0, 7); c.fill(); }
      c.globalAlpha = 1;
      const g = c.createRadialGradient(x, y, 1, x, y, 11); g.addColorStop(0, "#fff"); g.addColorStop(0.4, f.color); g.addColorStop(1, "rgba(255,120,200,0)");
      c.fillStyle = g; c.beginPath(); c.arc(x, y, 11, 0, 7); c.fill();
      break; }
    case "snipe": {
      c.globalAlpha = 1 - k;
      c.strokeStyle = "#fff"; c.lineWidth = 3; c.beginPath(); c.moveTo(f.x, f.y); c.lineTo(f.x2!, f.y2!); c.stroke();
      c.strokeStyle = f.color; c.lineWidth = 7; c.globalAlpha = (1 - k) * 0.35; c.beginPath(); c.moveTo(f.x, f.y); c.lineTo(f.x2!, f.y2!); c.stroke();
      break; }
    case "nova": {
      const r = (f.big ? 140 : 90) * (0.25 + k * 0.75);
      c.globalAlpha = (1 - k) * 0.8; c.strokeStyle = f.color; c.lineWidth = 5 * (1 - k) + 1;
      c.beginPath(); c.arc(f.x, f.y, r, 0, 7); c.stroke();
      c.fillStyle = "rgba(190,240,255,.18)"; c.fill();
      break; }
    case "boom": {
      const r = (f.big ? 54 : 34) * (0.3 + k * 0.7);
      const g = c.createRadialGradient(f.x, f.y, 1, f.x, f.y, r); g.addColorStop(0, "rgba(255,255,220,.95)"); g.addColorStop(0.4, "rgba(255,170,40,.8)"); g.addColorStop(1, "rgba(220,60,20,0)");
      c.globalAlpha = 1 - k * 0.9; c.fillStyle = g; c.beginPath(); c.arc(f.x, f.y, r, 0, 7); c.fill();
      break; }
    case "pop": {
      c.globalAlpha = 1 - k;
      const rr = (f.big ? 32 : 17) * (0.35 + k);
      c.fillStyle = f.color;
      for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2 + 0.3; c.beginPath(); c.arc(f.x + Math.cos(a) * rr, f.y + Math.sin(a) * rr, 3.2 * (1 - k) + 1, 0, 7); c.fill(); }
      c.strokeStyle = "#fff"; c.lineWidth = 3;
      for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; c.beginPath(); c.moveTo(f.x + Math.cos(a) * rr * 0.4, f.y + Math.sin(a) * rr * 0.4); c.lineTo(f.x + Math.cos(a) * rr * 0.9, f.y + Math.sin(a) * rr * 0.9); c.stroke(); }
      break; }
    case "text": {
      c.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
      c.font = "900 18px Outfit, sans-serif"; c.textAlign = "center";
      c.lineWidth = 4; c.strokeStyle = "#2a1f3d"; c.fillStyle = f.color;
      const y = f.y - k * 34;
      c.strokeText(f.text!, f.x, y); c.fillText(f.text!, f.x, y);
      break; }
    case "flash": {
      c.globalAlpha = (1 - k) * 0.9;
      const g = c.createRadialGradient(f.x, f.y, 1, f.x, f.y, 18); g.addColorStop(0, "#fff"); g.addColorStop(1, "rgba(255,220,120,0)");
      c.fillStyle = g; c.beginPath(); c.arc(f.x, f.y, 18, 0, 7); c.fill();
      break; }
  }
  c.restore();
}

export function ringFor(c: CanvasRenderingContext2D, x: number, y: number, range: number, ok = true) {
  const r = Math.min(range, 14) * CELL;
  c.save();
  c.fillStyle = ok ? "rgba(255,255,255,.2)" : "rgba(255,60,60,.28)";
  c.strokeStyle = ok ? "rgba(255,255,255,.9)" : "rgba(255,90,90,.95)";
  c.lineWidth = 3;
  c.beginPath();
  c.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, r, 0, Math.PI * 2);
  c.fill();
  c.stroke();
  c.restore();
}
